import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GouvGeorisquesDto, GouvGeorisquesRisqueDto } from '@workspace/data-types';

export interface EnrichedRiskItem {
  key: string;
  libelle: string;
  present: boolean;
  precision?: string | null;
  icon: string;
}

@Component({
  selector: 'etxe-property-geohazards-card',
  standalone: true,
  imports: [CommonModule, MatExpansionModule, MatIconModule, MatTooltipModule],
  templateUrl: './geohazards-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyGeohazardsCardComponent {
  geohazard = input<GouvGeorisquesDto | null | undefined>();
  addressLabel = input<string | null | undefined>();
  expanded = model<boolean>(false);

  naturalRiskItems = computed<EnrichedRiskItem[]>(() => {
    const geo = this.geohazard();
    if (!geo?.risquesNaturels) return [];
    const rn = geo.risquesNaturels;

    const list: { key: string; risk: GouvGeorisquesRisqueDto | undefined; icon: string }[] = [
      { key: 'inondation', risk: rn.inondation, icon: 'water_drop' },
      { key: 'retraitGonflementArgile', risk: rn.retraitGonflementArgile, icon: 'layers' },
      { key: 'seisme', risk: rn.seisme, icon: 'vibration' },
      { key: 'feuForet', risk: rn.feuForet, icon: 'local_fire_department' },
      { key: 'radon', risk: rn.radon, icon: 'bubble_chart' },
      { key: 'mouvementTerrain', risk: rn.mouvementTerrain, icon: 'landscape' },
      { key: 'remonteeNappe', risk: rn.remonteeNappe, icon: 'water' },
      { key: 'avalanche', risk: rn.avalanche, icon: 'downhill_skiing' },
    ];

    const result: EnrichedRiskItem[] = [];
    for (const item of list) {
      const risk = item.risk;
      if (risk) {
        result.push({
          key: item.key,
          libelle: risk.libelle,
          present: Boolean(risk.present),
          precision: risk.libelleStatutAdresse || risk.specifique || risk.libelleStatutCommune,
          icon: item.icon,
        });
      }
    }
    return result;
  });

  technologicalRiskItems = computed<EnrichedRiskItem[]>(() => {
    const geo = this.geohazard();
    if (!geo?.risquesTechnologiques) return [];
    const rt = geo.risquesTechnologiques;

    const list: { key: string; risk: GouvGeorisquesRisqueDto | undefined; icon: string }[] = [
      { key: 'icpe', risk: rt.icpe, icon: 'factory' },
      { key: 'nucleaire', risk: rt.nucleaire, icon: 'science' },
      { key: 'pollutionSols', risk: rt.pollutionSols, icon: 'terrain' },
      {
        key: 'canalisationsMatieresDangereuses',
        risk: rt.canalisationsMatieresDangereuses,
        icon: 'plumbing',
      },
      { key: 'ruptureBarrage', risk: rt.ruptureBarrage, icon: 'waves' },
      { key: 'risqueMinier', risk: rt.risqueMinier, icon: 'hardware' },
    ];

    const result: EnrichedRiskItem[] = [];
    for (const item of list) {
      const risk = item.risk;
      if (risk) {
        result.push({
          key: item.key,
          libelle: risk.libelle,
          present: Boolean(risk.present),
          precision: risk.libelleStatutAdresse || risk.specifique || risk.libelleStatutCommune,
          icon: item.icon,
        });
      }
    }
    return result;
  });

  detectedNaturalRisks = computed(() => this.naturalRiskItems().filter((r) => r.present));
  safeNaturalRisks = computed(() => this.naturalRiskItems().filter((r) => !r.present));

  detectedTechnoRisks = computed(() => this.technologicalRiskItems().filter((r) => r.present));
  safeTechnoRisks = computed(() => this.technologicalRiskItems().filter((r) => !r.present));

  totalDetectedCount = computed(() => this.detectedNaturalRisks().length + this.detectedTechnoRisks().length);

  detectedRisksLabel = computed(() => {
    const count = this.totalDetectedCount();
    return count > 1 ? `${count} risques identifiés` : `${count} risque identifié`;
  });

  naturalRisksCountLabel = computed(() => {
    const count = this.detectedNaturalRisks().length;
    const total = this.naturalRiskItems().length;
    return `${count} / ${total} ${count > 1 ? 'présents' : 'présent'}`;
  });

  technoRisksCountLabel = computed(() => {
    const count = this.detectedTechnoRisks().length;
    const total = this.technologicalRiskItems().length;
    return `${count} / ${total} ${count > 1 ? 'présents' : 'présent'}`;
  });
}
